/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import { Injectable } from '@angular/core';
import { stateTree } from 'ng/modules/core/constants/state-tree.constants';
import { INavState } from '../navigation.types';

/**
 * Extended NavState interface to include the next state to navigate to.
 */
interface IExtendedNavState extends INavState {
    nextState?: string | undefined;
}

export const NAVIGATION_SERVICE_TOKEN = 'navigationService';

/**
 * Create a hash of state names to the navigation state definition.
 */
const createStateHash = (states: INavState[]): Record<string, IExtendedNavState> => {
    return states.reduce((hash, navState, index) => {
        let childHash = {};

        if (navState.children) {
            childHash = createStateHash(navState.children);
        }

        const output = {
            ...hash,
            [navState.name]: {
                ...navState,
                nextState: states[index + 1]?.name,
            },
            ...childHash,
        };

        return output;
    }, {});
};

/**
 * @desc Service in charge of checking the relationship and order of states in the state tree.
 * @author alextsg
 */
@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    /**
     * Hash of state names to their navigation state declarations.
     */
    private stateHash = createStateHash(stateTree);

    /**
     * Return true if a given state name exists in the state hash.
     */
    public isDefined(stateName: string): boolean {
        return stateName in this.stateHash;
    }

    /**
     * Return the children states from a root state name.
     */
    public getChildren(stateName: string): INavState[] {
        return this.stateHash[stateName].children;
    }

    /**
     * Return the list of children state names given a parent state name.
     */
    public getChildrenStateNames(stateName: string): string[] | undefined {
        return this.getChildren(stateName)?.map(state => state.name);
    }

    /**
     * Return the next sibling state in navigation given a state name.
     */
    public getNextSiblingState(stateName: string): string | undefined {
        return this.stateHash[stateName].nextState;
    }
}
